import React from "react";
import { match } from "react-router-dom";
import { History, Location } from "history";
import TagsTables from "@/modules/shared/ui/TagsTables";
import { FormattedMessage } from "react-intl";
import {
    mapDataToCategories,
    mapDataToCustomTags,
    Columns,
    updateTagsConfig,
    onChangeUpdateTags,
    type TagsConfig,
    type TagUpdateValue,
} from "@/modules/shared/ui/ReactTableTags";
import EntityEditTemplate from "../../shared/EntityEditTemplate";
import { PLANT_TAGS } from "@/lib/ManufacturingTagsEditor.generated";
import { PLANT_TAGS_ACCESS } from "@/lib/ManufacturingTags.generated";
import { getBackend as getBackend2 } from "@/lib/backend/backend2";
import * as BusinessLogic from "@/lib/businessLogic";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import type { IUpdatePlantReq } from "@/lib/backend/manufacturing2.generated.types";
import type { ITags } from "@/lib/models";

import ErrorComponent from "@/modules/shared/ui/ErrorComponent";

import Loader from "@/modules/shared/ui/Loader";
import UserDocumentationLink from "@/modules/shared/UserDocumentationLink";

type Props = {
    match: match<{ uuid?: string }>;
    history: History;
    location: Location;
};

type State = {
    tags: ITags;
    title: string;
    error: string | null;
    weight: number;
    loading: boolean;
};

class PlantEdit extends React.Component<Props, State> {
    state: State = {
        error: null,
        tags: {},
        title: "",
        weight: 0,
        loading: false,
    };

    componentDidMount() {
        this.load();
    }

    getPlantUuid = () => {
        return this.props.match.params.uuid;
    };

    load = async () => {
        try {
            this.setState({ error: null });
            const res = await getBackend2().manufacturing.getPlants({});
            if (res.plants.length > 0) {
                const plant = res.plants.find(p => p.uuid === this.getPlantUuid());
                if (plant) {
                    this.setState({ title: plant.title, tags: plant.tags ?? {}, weight: plant.weight });
                }
            }
        } catch (e) {
            this.setState({
                error: translate("common.error", "An error occured while fetching data."),
            });
        }
    };

    onChangeCell = (name: string) => (value: TagUpdateValue) => {
        this.setState({ tags: onChangeUpdateTags(this.state.tags, name, value) });
    };

    renderError = () => {
        if (this.state.error) {
            return (
                <div>
                    <ErrorComponent msg={this.state.error} type="error" />
                    <button className="btn btn-outline-primary" onClick={() => this.onBack()}>
                        <FormattedMessage id="common.ok" defaultMessage="Ok" />
                    </button>
                </div>
            );
        }
        return null;
    };

    onSave = async () => {
        const save_error = translate("common.error_save", "An error occured while saving data.");
        try {
            const uuid = this.getPlantUuid();
            const params: IUpdatePlantReq = {
                uuid: uuid as string,
                tags: this.state.tags,
                weight: this.state.weight,
            };
            this.setState({ error: null, loading: true });

            await getBackend2().manufacturing.updatePlant(params);
            await BusinessLogic.forceManufacturingCacheRefresh();
            this.props.history.push(`/digital-twin/resources/plants/${uuid}`);
        } catch (e) {
            const detail = (e as Error).message;
            this.setState({
                error: detail ? `${save_error} (${detail})` : save_error,
                loading: false,
            });
        }
    };

    onBack = () => {
        const uuid = this.getPlantUuid();
        this.props.history.push(`/digital-twin/resources/plants/${uuid}`);
    };

    /**
     * Handling change of weight input component.
     */
    handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        // read new values
        const target = event.currentTarget;
        let value = target.value;
        const name = target.name;

        this.setState(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    render() {
        if (this.state.loading === true) {
            return (
                <div className="container">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="white_box">
                                <div className="tab-content">
                                    {this.state.error ? (
                                        this.renderError()
                                    ) : (
                                        <div>
                                            <FormattedMessage
                                                id="common.add_line_loading"
                                                defaultMessage="Adding new line to the knowledge graph. Initialisation may take several minutes..."
                                            />
                                            <Loader small={true} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else {
            return (
                <EntityEditTemplate onSave={this.onSave} onBack={this.onBack} title={this.state.title}>
                    {this.renderError()}
                    <div className="tab-content">
                        <div className="form-group">
                            <label>
                                <FormattedMessage id="common.weight" defaultMessage="Weight" />:
                            </label>
                            <input
                                name="weight"
                                type="text"
                                className="form-control"
                                value={this.state.weight}
                                onChange={this.handleInputChange}
                            />
                        </div>
                    </div>
                    <div className="text-right mb-3">
                        <UserDocumentationLink link_key={"plants"} />
                    </div>
                    <TagsTables
                        categories={mapDataToCategories(
                            this.state.tags,
                            updateTagsConfig(PLANT_TAGS as TagsConfig),
                            PLANT_TAGS_ACCESS
                        )}
                        columns={Columns}
                        onChangeCell={this.onChangeCell}
                        customTags={mapDataToCustomTags(this.state.tags, updateTagsConfig(PLANT_TAGS as TagsConfig))}
                    />
                </EntityEditTemplate>
            );
        }
    }
}

export default PlantEdit;
